import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  page: { path: '/point-item-note', pageLabel: '포인트 코인 아이템' },
  pointSet: {
    path: '/point-item-note',
    openSelector: '[data-tutorial-pin="open-point-set"]',
    pageLabel: '포인트 코인 아이템',
    openLabel: '포인트 세트 설정',
  },
  itemCard: {
    path: '/point-item-note',
    openSelector: '[data-tutorial-pin="open-item-card"]',
    pageLabel: '포인트 코인 아이템',
    openLabel: '아이템 카드 설정',
  },
  level: {
    path: '/point-item-note',
    openSelector: '[data-tutorial-pin="open-level"]',
    pageLabel: '포인트 코인 아이템',
    openLabel: '레벨 설정',
  },
  bulkPoint: {
    path: '/point-item-note',
    openSelector: '[data-tutorial-pin="bulk"]',
    pageLabel: '포인트 코인 아이템',
    openLabel: '일괄 포인트 지급',
  },
  store: {
    path: '/point-item-note',
    openSelector: '[data-tutorial-pin="store-btn"]',
    pageLabel: '포인트 코인 아이템',
    openLabel: '스토어',
  },
  history: {
    path: '/point-item-note',
    openSelector: '[data-tutorial-pin="stu-card"]',
    pageLabel: '포인트 코인 아이템',
    openLabel: '학생 카드(이력)',
  },
  inventory: {
    path: '/point-item-note',
    openSelector: '[data-tutorial-pin="stu-card"]',
    pageLabel: '포인트 코인 아이템',
    openLabel: '학생 카드(인벤토리)',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-point-item-note': R.page,
  'select-group': R.page,
  'point-set-open': R.page,
  'point-set-tabs': R.pointSet,
  'point-set-list': R.pointSet,
  'point-set-save': R.pointSet,
  'item-card-open': R.page,
  'item-card-tab-levelup': R.itemCard,
  'item-card-goto-assignment': R.itemCard,
  'item-card-tab-assignment': R.itemCard,
  'item-card-goto-store': R.itemCard,
  'item-card-tab-store': R.itemCard,
  'item-card-add': R.itemCard,
  'item-card-type-explain': R.itemCard,
  'item-card-type-active': R.itemCard,
  'item-card-use-count': R.itemCard,
  'item-card-cancel': R.itemCard,
  'item-card-tab-profile': R.itemCard,
  'item-card-profile-toggle': R.itemCard,
  'item-card-profile-price': R.itemCard,
  'item-card-admin-set': R.itemCard,
  'item-card-profile-manage': R.itemCard,
  'item-card-profile-add': R.itemCard,
  'item-card-save': R.itemCard,
  'level-open': R.page,
  'level-basic': R.level,
  'level-reward-tab': R.level,
  'level-reward': R.level,
  'level-save': R.level,
  'switch-record-mode': R.page,
  'toolbar-date': R.page,
  'toolbar-timetag': R.page,
  'toolbar-layout': R.page,
  'switch-points-tab': R.page,
  'adjust-points': R.page,
  'save-points': R.page,
  'select-members-bulk': R.page,
  'open-bulk-points': R.page,
  'bulk-points-modal': R.bulkPoint,
  'switch-coins-tab': R.page,
  'coins-guide': R.page,
  'switch-items-tab': R.page,
  'open-member-store': R.page,
  'member-store-modal': R.store,
  'save-item-purchase': R.page,
  'switch-notes-tab': R.page,
  'notes-guide': R.page,
  'switch-view-mode': R.page,
  'switch-points-view': R.page,
  'open-history': R.page,
  'history-modal': R.history,
  'switch-coins-view': R.page,
  'open-coin-history': R.page,
  'coin-history-modal': R.history,
  'switch-items-view': R.page,
  'open-inventory': R.page,
  'inventory-modal': R.inventory,
};

/** 설정 모달 마지막 단계: 안은 자유 조작, 「다음」또는 모달 닫기로 진행 */
const SETTINGS_MODAL_DISMISS = {
  manualAdvance: true as const,
  allowManualAdvanceActions: true as const,
};

const pointCoinItemRaw: TutorialDefinition = {
  id: 'point-coin-item',
  title: '포인트·코인·아이템 관리',
  summary:
    '포인트 세트·아이템 카드·레벨 설정부터 기록 모드 지급, 조회 모드 이력·인벤토리까지 한 번에 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '포인트·코인·아이템 관리 튜토리얼을 시작해요! 세트 설정, 지급, 조회까지 순서대로 알려드릴게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '포인트·코인·아이템을 시작해요!',
      placement: 'center',
    },
    {
      id: 'go-point-item-note',
      mascot: '사이드바에서 "포인트 노트"를 눌러 이동해 주세요.',
      targetSelector: 'a[href^="/point-item-note"]',
      placement: 'right',
      section: '[포인트·코인·아이템] 시작',
      sectionMascot: '포인트 코인 아이템 페이지로 이동하는 구간이에요.',
    },
    {
      id: 'select-group',
      mascot:
        '지급·조회는 선택한 멤버 그룹 기준으로 보여요. 위쪽에서 그룹을 골라 주세요. 고른 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-apn="group-select"]',
      manualAdvance: true,
      section: '[포인트·코인·아이템] 그룹 선택',
      sectionMascot: '포인트·코인·아이템을 다룰 멤버 그룹을 고르는 구간이에요.',
    },

    // —— 포인트 세트: 설명(잠금) → 전체 하이라이트에서 설정·저장 ——
    {
      id: 'point-set-open',
      mascot: '오른쪽 위 "포인트 세트 설정" 버튼을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="open-point-set"]',
      section: '[설정] 포인트 세트',
      sectionMascot: '긍정·행동체크 포인트 종류를 만드는 구간이에요.',
    },
    {
      id: 'point-set-tabs',
      mascot:
        '"긍정행동 포인트"와 "행동체크 포인트" 탭으로 종류를 나눠 관리해요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="point-set-tabs"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'point-set-list',
      mascot:
        '목록이 실제 지급에 쓰이는 세트예요. "새 세트 추가"로 더 만들 수 있어요. 지금은 눈으로만 보고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="point-set-body"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'point-set-save',
      mascot:
        '모달 안에서 세트를 추가·수정해 볼 수 있어요. 준비가 되면 "다음"을 누르거나 모달을 닫아 주세요.',
      targetSelector: '[data-tutorial-pin="point-set-modal"]',
      advanceWhenSelectorGone: '[data-tutorial-pin="point-set-modal"]',
      ...SETTINGS_MODAL_DISMISS,
    },

    // —— 아이템 카드: 카테고리 순회 → 등록(패시브/액티브) → 프로필 카드 ——
    {
      id: 'item-card-open',
      mascot: '"아이템 카드 설정" 버튼을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="open-item-card"]',
      section: '[설정] 아이템 카드',
      sectionMascot: '레벨업·과제보상·스토어·프로필 카드를 만드는 구간이에요.',
    },
    {
      id: 'item-card-tab-levelup',
      mascot:
        '"레벨업 아이템"은 학생이 레벨업할 때 보상으로 줄 수 있는 카드예요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: 'text:레벨업 아이템@[data-tutorial-pin="item-card-tabs"] button',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'item-card-goto-assignment',
      mascot: '이어서 "과제보상 아이템" 탭을 눌러 주세요.',
      targetSelector: 'text:과제보상 아이템@[data-tutorial-pin="item-card-tabs"] button',
      advanceImmediately: true,
    },
    {
      id: 'item-card-tab-assignment',
      mascot:
        '"과제보상 아이템"은 과제 달성·완료 보상으로 지급할 카드예요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: 'text:과제보상 아이템@[data-tutorial-pin="item-card-tabs"] button',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'item-card-goto-store',
      mascot: '이어서 "스토어 아이템" 탭을 눌러 주세요.',
      targetSelector: 'text:스토어 아이템@[data-tutorial-pin="item-card-tabs"] button',
      advanceImmediately: true,
    },
    {
      id: 'item-card-tab-store',
      mascot:
        '"스토어 아이템"은 학생이 코인으로 구입하는 카드예요. 가격·설명을 정해 스토어에 올려요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        'text:스토어 아이템@[data-tutorial-pin="item-card-tabs"] button, [data-tutorial-pin="item-card-body"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'item-card-add',
      mascot: '"아이템 추가"를 눌러 새 아이템 등록 블럭을 열어 주세요.',
      targetSelector: '[data-tutorial-pin="item-card-add"]',
      advanceWhenSelectorExists: '[data-tutorial-pin="item-card-type-toggle"]',
    },
    {
      id: 'item-card-type-explain',
      mascot:
        '"패시브"는 보유만으로 의미있는 인증 카드, 미션 달성 카드 등으로 사용 버튼이 없어요. "액티브"는 인벤토리에서 직접 사용하며, 사용 횟수가 줄어들어요. 지금은 설명만 듣고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="item-card-type-toggle"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'item-card-type-active',
      mascot: '패시브/액티브 버튼을 눌러 "액티브"로 바꿔 주세요.',
      targetSelector: '[data-tutorial-pin="item-card-type-toggle"]',
      advanceWhenSelectorExists: '[data-tutorial-pin="item-card-type-toggle"].is-active',
      manualAdvanceIfExists: '[data-tutorial-pin="item-card-type-toggle"].is-active',
    },
    {
      id: 'item-card-use-count',
      mascot:
        '"사용 횟수"는 액티브 아이템을 몇 번까지 쓸 수 있는지예요. 소진되면 인벤토리에서 더 이상 사용할 수 없어요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="item-card-use-count"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'item-card-cancel',
      mascot: '연습이니 "취소"를 눌러 등록 블럭을 닫아 주세요.',
      targetSelector: '[data-tutorial-pin="item-card-cancel"]',
      advanceWhenSelectorGone: '[data-tutorial-pin="item-card-type-toggle"]',
    },
    {
      id: 'item-card-tab-profile',
      mascot: '이제 "프로필 카드" 탭을 눌러 주세요.',
      targetSelector: 'text:프로필 카드@[data-tutorial-pin="item-card-tabs"] button',
      advanceWhenSelectorExists: '[data-tutorial-pin="item-card-profile-body"]',
      manualAdvanceIfExists: '[data-tutorial-pin="item-card-profile-body"]',
    },
    {
      id: 'item-card-profile-toggle',
      mascot:
        '"사용 중"으로 표시하면 프로필 이미지 선택 모달과 스토어에 해당 세트가 보이며, "미사용"으로 설정하면 보이지 않아요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="profile-set-toggle"]',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '표시할 프로필 카드 세트가 없으면 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'item-card-profile-price',
      mascot:
        '코인 칸에서 해당 프로필 카드 세트 랜덤 뽑기 1회 가격을 정할 수 있어요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="profile-set-price"]',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '표시할 프로필 카드 세트가 없으면 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'item-card-admin-set',
      mascot:
        '관리자 제공 프로필 카드 세트(초록 점)를 눌러 관리 모달을 열어 주세요. 세트가 없으면 "다음"으로 넘어가도 돼요.',
      targetSelector: '[data-tutorial-pin="profile-set-admin"]',
      advanceWhenSelectorExists: '[data-tutorial-pin="profile-card-manage-modal"]',
      manualAdvance: true,
      manualAdvanceSkipToStepId: 'item-card-profile-add',
      targetMissingHint:
        '관리자 제공 세트가 아직 없으면 "다음"으로 넘어가도 돼요. 이어서 직접 세트 추가 안내가 나와요.',
    },
    {
      id: 'item-card-profile-manage',
      mascot:
        '위쪽 "확률/환급" 안내를 보세요. 뽑기 확률은 보통 65% · 레어 20% · 희귀 10% · 초희귀 5%이고, 같은 카드를 중복으로 뽑으면 등급별 환급률(보통 10% · 레어 30% · 희귀 50% · 초희귀 120%)만큼 코인으로 돌려받아요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-pin="profile-card-manage-modal"], [data-tutorial-pin="profile-card-rarity-guide"]',
      advanceWhenSelectorGone: '[data-tutorial-pin="profile-card-manage-modal"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      targetMissingHint:
        '프로필 카드 관리 모달이 닫혀 있으면 관리자 세트를 다시 열거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'item-card-profile-add',
      mascot:
        '"프로필 카드 세트 추가"로 직접 카드 세트를 구성할 수도 있어요. 지금은 눌러보지 말고 위치만 기억한 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="profile-card-set-add"]',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '사용자 세트를 이미 모두 만들어 추가 버튼이 비활성화됐을 수 있어요. "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'item-card-save',
      mascot:
        '모달 안에서 아이템·프로필 카드 설정을 추가·수정해 볼 수 있어요. 준비가 되면 "다음"을 누르거나 모달을 닫아 주세요.',
      targetSelector: '[data-tutorial-pin="item-card-modal"]',
      advanceWhenSelectorGone: '[data-tutorial-pin="item-card-modal"]',
      ...SETTINGS_MODAL_DISMISS,
    },

    // —— 레벨: 설명(잠금) → 전체에서 둘러보기 ——
    {
      id: 'level-open',
      mascot: '"레벨 설정"(LV) 버튼을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="open-level"]',
      section: '[설정] 레벨',
      sectionMascot: '레벨업 기준과 레벨별 보상을 정하는 구간이에요.',
    },
    {
      id: 'level-basic',
      mascot:
        '"기본 설정"에서 레벨업에 필요한 포인트를 정해요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="level-basic"], [data-tutorial-pin="level-tabs"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'level-reward-tab',
      mascot: '"레벨별 보상 설정" 탭을 눌러 주세요.',
      targetSelector: 'text:레벨별 보상 설정@[data-tutorial-pin="level-tabs"] button',
      advanceWhenSelectorExists: '[data-tutorial-pin="level-reward"]',
      manualAdvanceIfExists: '[data-tutorial-pin="level-reward"]',
    },
    {
      id: 'level-reward',
      mascot:
        '레벨마다 보상 코인·레벨업 아이템을 지정할 수 있어요. 지금은 눈으로만 보고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="level-reward"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'level-save',
      mascot:
        '모달 안에서 레벨업 포인트·보상을 설정해 볼 수 있어요. 준비가 되면 "다음"을 누르거나 모달을 닫아 주세요.',
      targetSelector: '[data-tutorial-pin="level-modal"]',
      advanceWhenSelectorGone: '[data-tutorial-pin="level-modal"]',
      ...SETTINGS_MODAL_DISMISS,
    },

    // —— 기록 모드 · 개별 포인트(+ → 저장) ——
    {
      id: 'switch-record-mode',
      mascot:
        '이제 실제로 지급해볼게요. 오른쪽 위 모드 버튼을 눌러 "전체 기록 모드"로 전환해 주세요.',
      targetSelector: '[data-tutorial-pin="mode"]',
      manualAdvanceIfExists: '.cp-pin-mode-pill.is-record',
      section: '[지급] 기록 모드 · 포인트',
      sectionMascot: '기록 모드에서 학생에게 포인트·코인·아이템을 넣는 구간이에요.',
    },
    {
      id: 'toolbar-date',
      mascot:
        '날짜 칩은 지급이 기록될 날짜예요. 지금은 바꾸지 말고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="date"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'toolbar-timetag',
      mascot:
        '타임 태그는 수업 시수·시간대에 맞춰 기록을 구분할 때 써요. 지금은 설명만 듣고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="timetag"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'toolbar-layout',
      mascot:
        '"번호 / 자리"로 학생 카드 배치를 바꿀 수 있어요. 지금은 그대로 두고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="layout"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'switch-points-tab',
      mascot: '"포인트" 탭이 선택돼 있는지 확인해 주세요. 아니면 "포인트"를 눌러 주세요.',
      targetSelector: 'text:포인트@[data-tutorial-pin="data-mode"] button',
      manualAdvanceIfExists: '[data-tutorial-pin="data-mode"].is-points',
    },
    {
      id: 'adjust-points',
      mascot:
        '지급할 학생 카드의 "+" 증가 버튼을 눌러 포인트 값을 바꿔 주세요. 바꾼 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="stu-card"] .cp-pin-stu-ctrl-btn[aria-label="증가"]',
      manualAdvance: true,
    },
    {
      id: 'save-points',
      mascot: '"저장하기"를 눌러 포인트 지급을 반영해 주세요.',
      targetSelector: '[data-tutorial-pin="save"]:not([disabled])',
      manualAdvance: true,
      targetMissingHint:
        '값을 바꾼 뒤 "저장하기"가 활성화되면 눌러 주세요. 이미 저장했거나 변경이 없다면 "다음"으로 넘어가도 돼요.',
    },

    // —— 포인트 일괄 지급 (프로필 선택 → 일괄 모달) ——
    {
      id: 'select-members-bulk',
      mascot:
        '여러 명에게 한 번에 주려면 학생 프로필(아바타)을 눌러 선택해 주세요. 한 명 이상 고른 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="stu-card"] .cp-pin-stu-avatar',
      manualAdvance: true,
      manualAdvanceEnableWhen: '.cp-pin-stu-card.is-selected',
      section: '[지급] 일괄 포인트',
      sectionMascot: '선택한 멤버에게 일괄로 포인트를 넣는 구간이에요.',
    },
    {
      id: 'open-bulk-points',
      mascot: '"일괄 포인트 지급"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="bulk"]:not([disabled])',
      advanceWhenSelectorExists: '[data-tutorial-pin="bulk-modal"]',
      targetMissingHint:
        '학생을 먼저 선택한 뒤 "일괄 포인트 지급"이 활성화되면 눌러 주세요.',
    },
    {
      id: 'bulk-points-modal',
      mascot:
        '세트·점수·메모를 정한 뒤 "블럭에 반영" 또는 "포인트 지급"을 눌러 주세요. 닫기로는 넘어갈 수 없어요.',
      targetSelector: '[data-tutorial-pin="bulk-modal"]',
      advanceSelector:
        '[data-tutorial-pin="bulk-footer"] .cp-pin-modal-btn--primary',
      blockClickSelectors: '.cp-pin-modal-btn--secondary, .cp-pin-modal-close',
    },

    // —— 코인 (설명 위주) ——
    {
      id: 'switch-coins-tab',
      mascot: '코인도 같은 방식으로 지급해요. "코인" 탭을 눌러 주세요.',
      targetSelector: 'text:코인@[data-tutorial-pin="data-mode"] button',
      manualAdvanceIfExists: '[data-tutorial-pin="data-mode"].is-coins',
      section: '[지급] 코인·아이템',
      sectionMascot: '코인 지급과 스토어 아이템 구입 흐름을 보는 구간이에요.',
    },
    {
      id: 'coins-guide',
      mascot:
        '코인은 카드의 +/-로 양을 정하거나, 프로필 선택 후 일괄 코인 지급으로 넣을 수 있어요. 지금은 설명만 듣고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="stu-card"], [data-tutorial-pin="actions"]',
      manualAdvance: true,
      blockTargetClick: true,
    },

    // —— 학생 블럭 스토어로 구입 ——
    {
      id: 'switch-items-tab',
      mascot: '"아이템" 탭을 눌러 주세요.',
      targetSelector: 'text:아이템@[data-tutorial-pin="data-mode"] button',
      manualAdvanceIfExists: '[data-tutorial-pin="data-mode"].is-items',
    },
    {
      id: 'open-member-store',
      mascot: '구입할 학생 블럭의 스토어 버튼을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="store-btn"]',
      advanceWhenSelectorExists: '[data-tutorial-pin="store-modal"]',
      manualAdvanceIfExists: '[data-tutorial-pin="store-modal"]',
    },
    {
      id: 'member-store-modal',
      mascot:
        '스토어에서 아이템 카드를 고른 뒤 "장바구니 담기"를 눌러 주세요. 담았으면 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="store-modal"]',
      advanceSelector: '[data-tutorial-pin="store-buy"]:not([disabled])',
      advanceImmediately: true,
      advanceWhenSelectorGone: '[data-tutorial-pin="store-modal"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      targetMissingHint:
        '스토어를 열어 주세요. 아이템이 없으면 아이템 카드 설정의 스토어 탭에 추가한 뒤 다시 진행하거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'save-item-purchase',
      mascot:
        '학생 블럭 장바구니에 담긴 아이템을 확인한 뒤, "구매하기"를 눌러 반영해 주세요.',
      targetSelector: '[data-tutorial-pin="save"]:not([disabled])',
      manualAdvance: true,
      targetMissingHint:
        '장바구니에 담으면 "구매하기"가 활성화돼요. 이미 반영했거나 담은 아이템이 없으면 "다음"으로 넘어가도 돼요.',
    },

    // —— 기록 모드 · 기록 유형 ——
    {
      id: 'switch-notes-tab',
      mascot: '"기록" 탭을 눌러 주세요.',
      targetSelector: 'text:기록@[data-tutorial-pin="data-mode"] button',
      manualAdvanceIfExists: '[data-tutorial-pin="data-mode"].is-notes',
      section: '[지급] 기록',
      sectionMascot: '학생별 짧은 노트를 남기는 구간이에요.',
    },
    {
      id: 'notes-guide',
      mascot:
        '학생 카드에서 기록 유형(일반·학습·행동)을 바꾸고 메모를 적을 수 있어요. 여러 명을 고른 뒤 "일괄 기록 작성"으로도 남길 수 있어요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-pin="stu-card"], [data-tutorial-pin="note-type"], [data-tutorial-pin="actions"]',
      manualAdvance: true,
      blockTargetClick: true,
    },

    // —— 조회 모드 · 포인트/코인 이력 · 인벤토리 ——
    {
      id: 'switch-view-mode',
      mascot: '지급을 마쳤다면 모드 버튼을 눌러 "개별 기록 모드"로 바꿔 주세요.',
      targetSelector: '[data-tutorial-pin="mode"]',
      manualAdvanceIfExists: '.cp-pin-mode-pill.is-view',
      section: '[조회] 이력·인벤토리',
      sectionMascot: '조회 모드에서 학생별 포인트·코인 이력과 보유 아이템을 확인하는 구간이에요.',
    },
    {
      id: 'switch-points-view',
      mascot: '포인트 이력을 보려면 "포인트" 탭을 눌러 주세요.',
      targetSelector: 'text:포인트@[data-tutorial-pin="data-mode"] button',
      manualAdvanceIfExists: '[data-tutorial-pin="data-mode"].is-points',
    },
    {
      id: 'open-history',
      mascot: '학생 카드를 눌러 포인트 이력을 열어 주세요.',
      targetSelector: '[data-tutorial-pin="stu-card"].is-view',
      advanceWhenSelectorExists: '[data-tutorial-pin="history-modal"]',
      manualAdvanceIfExists: '[data-tutorial-pin="history-modal"]',
    },
    {
      id: 'history-modal',
      mascot:
        '학생별 포인트 이력을 여기서 보고 수정·삭제할 수 있어요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="history-modal"]',
      advanceWhenSelectorGone: '[data-tutorial-pin="history-modal"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'switch-coins-view',
      mascot: '코인 이력을 보려면 "코인" 탭을 눌러 주세요.',
      targetSelector: 'text:코인@[data-tutorial-pin="data-mode"] button',
      manualAdvanceIfExists: '[data-tutorial-pin="data-mode"].is-coins',
    },
    {
      id: 'open-coin-history',
      mascot: '학생 카드를 눌러 코인 이력을 열어 주세요.',
      targetSelector: '[data-tutorial-pin="stu-card"].is-view',
      advanceWhenSelectorExists: '[data-tutorial-pin="history-modal"]',
      manualAdvanceIfExists: '[data-tutorial-pin="history-modal"]',
    },
    {
      id: 'coin-history-modal',
      mascot:
        '학생별 코인 획득·사용 이력을 확인할 수 있어요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="history-modal"]',
      advanceWhenSelectorGone: '[data-tutorial-pin="history-modal"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'switch-items-view',
      mascot: '보유 아이템을 보려면 "아이템" 탭을 눌러 주세요.',
      targetSelector: 'text:아이템@[data-tutorial-pin="data-mode"] button',
      manualAdvanceIfExists: '[data-tutorial-pin="data-mode"].is-items',
    },
    {
      id: 'open-inventory',
      mascot: '조회 모드에서 학생 카드를 눌러 인벤토리를 열어 주세요.',
      targetSelector: '[data-tutorial-pin="stu-card"].is-view',
      advanceWhenSelectorExists: '[data-tutorial-pin="inventory-modal"]',
      manualAdvanceIfExists: '[data-tutorial-pin="inventory-modal"]',
    },
    {
      id: 'inventory-modal',
      mascot:
        '학생이 가진 아이템 카드와 사용·삭제 기능을 볼 수 있어요. 살펴본 뒤 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-pin="inventory-modal"]',
      advanceWhenSelectorGone: '[data-tutorial-pin="inventory-modal"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'outro',
      mascot:
        '포인트·코인·아이템 관리 튜토리얼을 마쳤어요! 필요할 때 계정 메뉴의 "튜토리얼"에서 다시 볼 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const pointCoinItem: TutorialDefinition = {
  ...pointCoinItemRaw,
  steps: pointCoinItemRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    const targetMissingHint = buildTutorialTargetMissingHint(recover, step.targetMissingHint);
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint,
    };
  }),
};

export default pointCoinItem;
